/**
 * Lightweight imperative document head updater. Avoids react-helmet to keep deps tight.
 *
 * NOTE: this only affects the live <head> in the user's browser. Google's primary
 * crawler now executes JavaScript before indexing, so these tags work for SEO too.
 * For social-card crawlers (Twitter, Facebook) that don't run JS, the static
 * defaults in public/index.html act as the fallback.
 */
const TAGS_KEY = "__tizona_seo_tags__";
const SITE = "https://tizonatech.in";

function upsert(selector, build) {
    let el = document.head.querySelector(selector);
    if (!el) {
        el = build();
        document.head.appendChild(el);
    }
    return el;
}

function setMeta(attr, value, content) {
    const sel = `meta[${attr}="${value}"]`;
    const el = upsert(sel, () => {
        const m = document.createElement("meta");
        m.setAttribute(attr, value);
        return m;
    });
    el.setAttribute("content", content || "");
    return el;
}

function setCanonical(href) {
    const el = upsert('link[rel="canonical"]', () => {
        const l = document.createElement("link");
        l.setAttribute("rel", "canonical");
        return l;
    });
    el.setAttribute("href", href);
}

function setOrRemoveMeta(attr, value, content) {
    const existing = document.head.querySelector(`meta[${attr}="${value}"]`);
    if (!content) {
        if (existing) existing.remove();
        return;
    }
    setMeta(attr, value, content);
}

function setJsonLd(id, data) {
    let el = document.getElementById(id);
    if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = id;
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
}

function clearJsonLd(id) {
    const el = document.getElementById(id);
    if (el) el.remove();
}

/**
 * Apply SEO tags for the current view.
 *
 * @param {object} opts
 * @param {string} opts.title         Browser tab + og:title (kept short).
 * @param {string} opts.description   Meta description + og:description.
 * @param {string} opts.path          Canonical path, e.g. "/post/foo".
 * @param {string} [opts.image]       Absolute URL of share image (defaults to /og-image.png).
 * @param {"article"|"website"} [opts.type] OG type (default "website").
 * @param {object} [opts.jsonLd]      JSON-LD structured data for this page.
 * @param {string} [opts.publishedTime] ISO date — emitted as article:published_time (articles only).
 * @param {string} [opts.modifiedTime]  ISO date — emitted as article:modified_time (articles only).
 * @param {string} [opts.section]       Category — emitted as article:section (articles only).
 */
export function applySeo({ title, description, path, image, type = "website", jsonLd, publishedTime, modifiedTime, section }) {
    const fullTitle = title;
    const url = `${SITE}${path}`;
    const shareImage = image || `${SITE}/og-image.png`;

    document.title = fullTitle;
    setMeta("name", "description", description);
    setMeta("property", "og:type", type);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", shareImage);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", shareImage);
    setCanonical(url);

    // Article-scoped OG tags: set on articles, removed elsewhere so they
    // never leak onto the homepage/category pages.
    setOrRemoveMeta("property", "article:published_time", type === "article" ? publishedTime : null);
    setOrRemoveMeta("property", "article:modified_time", type === "article" ? modifiedTime : null);
    setOrRemoveMeta("property", "article:section", type === "article" ? section : null);

    if (jsonLd) {
        setJsonLd("tizona-page-jsonld", jsonLd);
    } else {
        clearJsonLd("tizona-page-jsonld");
    }

    // Stash a marker so debugging is easy.
    window[TAGS_KEY] = { title, path, type };
}

export const SITE_URL = SITE;
